import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  sendEmailSignInLink,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@aaas/shared';

export function EmailSentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email = location.state?.email || '';

  const handleResend = async () => {
    if (!email) {
      setError(t('emailSent.noEmailFound'));
      return;
    }

    setIsResending(true);
    setError(null);
    setResendSuccess(false);

    try {
      // Use VITE_APP_URL for callback to ensure correct domain
      const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
      await sendEmailSignInLink(email, `${appUrl}/auth/email-callback`);
      setResendSuccess(true);
    } catch (err) {
      console.error('Failed to resend email:', err);
      setError(t('login.errors.sendLinkFailed'));
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (!email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-gray-600">{t('emailSent.noEmailFound')}</p>
              <Button onClick={handleBackToLogin} className="w-full mt-4" variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('emailSent.backToLogin')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Mail className="mx-auto h-12 w-12 text-blue-600" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">{t('emailSent.heading')}</h2>
          <p className="mt-2 text-sm text-gray-600">{t('emailSent.subheading')}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-center">{t('emailSent.cardTitle')}</CardTitle>
            <CardDescription className="text-center">{t('emailSent.cardDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-sm text-blue-800">{t('emailSent.sentTo')}</p>
              <p className="font-medium text-blue-900 mt-1">{email}</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {resendSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm">
                {t('emailSent.resendSuccess')}
              </div>
            )}

            <div className="text-center text-sm text-gray-500 space-y-2">
              <p>{t('emailSent.expiry')}</p>
              <p>{t('emailSent.spamHint')}</p>
            </div>

            <div className="flex flex-col space-y-2">
              <Button
                onClick={handleResend}
                disabled={isResending}
                variant="outline"
                className="w-full"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isResending ? 'animate-spin' : ''}`} />
                {isResending ? t('emailSent.resendingButton') : t('emailSent.resendButton')}
              </Button>

              <Button onClick={handleBackToLogin} variant="ghost" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('emailSent.differentEmailButton')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
